import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { Placement, placements, viewport } from '@popperjs/core';

import { useTimeout } from '@lib/useTimeout';
import './tooltip.css';

export interface TooltipProps {
  children: React.ReactNode,
  popperContent: React.ReactNode,
  placement?: Placement,
  offset?: number,
  popoverPortalSelector?: string, // needs to be set if you want to use createPortal and set different location in the DOM for children, to ensure that content is rendered above other elements
  className?: string,
  showDelayMs?: number
}

export function Tooltip(props: TooltipProps) {
  const [showPopper, setShowPopper] = useState(false);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowRef, setArrowRef] = useState(null);
  const { addTimeout, timeoutIdsRef } = useTimeout();

  const popperSide = props.placement || 'top';
  const popperOffset = props.offset || 5; // avoid overlap on reference element
  const popperModifiers: any[] = [
    {
      name: 'arrow',
      options: { element: arrowRef }
    },
    {
      name: 'preventOverflow',
      options: {
        mainAxis: false
      },
    },
    {
      name: "offset",
      options: {
        offset: [0, popperOffset]
      }
    }
  ];

  if (popperSide === 'top' || popperSide === 'bottom') {
    popperModifiers.push({
      name: 'flip',
      options: {
        fallbackPlacements: [
          `${popperSide}-start`,
          `${popperSide}-end`
        ]
      },
    });
  }

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: popperSide,
    modifiers: popperModifiers,
    strategy: 'fixed'
  });

  useEffect(() => {
    // only listen for scroll while the popover is actually open, so a scroll that merely
    // brought the reference element into view (before it was hovered) can't immediately
    // hide the popover it's about to show
    if (!showPopper) {
      return;
    }
    const eventListener = () => {
      setShowPopper(false);
    };
    document.addEventListener('scroll', eventListener, true);
    return () => {
      document.removeEventListener('scroll', eventListener, true);
    };
  }, [showPopper]);

  let popover = (
    <div ref={setPopperElement} className={`tooltip_popover-element ${popperSide}`} style={{ ...styles.popper }} {...attributes.popper}>
      <div ref={setArrowRef} style={styles.arrow} className="tooltip-arrow" />
      <div className='tooltip_popover-content'>{props.popperContent}</div>
    </div>
  );

  if (props.popoverPortalSelector) {
    if (document.querySelector(props.popoverPortalSelector)) {
      popover = createPortal(popover, document.querySelector(props.popoverPortalSelector));
    }
  }

  return (
    <>
      <div ref={setReferenceElement} className={`${props.className} tooltip_reference-element`}
        onMouseOver={() => {
          if (props.showDelayMs) {
            addTimeout(() => setShowPopper(true), props.showDelayMs);
          } else {
            setShowPopper(true);
          }
        }}
        onMouseOut={() => {
          timeoutIdsRef.current.forEach(clearTimeout);
          timeoutIdsRef.current = [];
          setShowPopper(false);
        }}
      >
        {props.children}
      </div>
      {showPopper && popover}
    </>
  );
}
